import React from 'react';
import { useThemeStore } from '../../store/themeStore';

// Legacy variants (kept for backward compatibility)
import logoDark from '../../assets/brand/svg/01-primary-dark.svg';
import logoLight from '../../assets/brand/svg/02-primary-light.svg';
import logoTransparent from '../../assets/brand/svg/03-transparent.svg';
import logoMonoBlack from '../../assets/brand/svg/04-mono-black.svg';
import logoMonoWhite from '../../assets/brand/svg/05-mono-white.svg';
import logoTightCropDark from '../../assets/brand/svg/06-tight-crop-dark.svg';
import logoTightCropTransparent from '../../assets/brand/svg/07-tight-crop-transparent.svg';

// New animated brand variants
import logoStaticDark from '../../assets/brand/svg/01-static-dark.svg';
import logoStaticLight from '../../assets/brand/svg/02-static-light.svg';
import logoAttentionDark from '../../assets/brand/svg/03-attention-dark.svg';
import logoAttentionLight from '../../assets/brand/svg/04-attention-light.svg';
import logoSaluteDark from '../../assets/brand/svg/05-salute-dark.svg';
import logoSaluteLight from '../../assets/brand/svg/06-salute-light.svg';
import logoStaticTransparent from '../../assets/brand/svg/07-static-transparent.svg';

export type LogoVariant =
  | 'transparent'
  | 'dark'
  | 'light'
  | 'mono-black'
  | 'mono-white'
  | 'tight-crop-dark'
  | 'tight-crop-transparent'
  | 'static-dark'
  | 'static-light'
  | 'static-transparent'
  | 'attention-dark'
  | 'attention-light'
  | 'salute-dark'
  | 'salute-light';

/** Variants that contain CSS animations requiring <object> instead of <img> */
const ANIMATED_VARIANTS = new Set<LogoVariant>([
  'attention-dark',
  'attention-light',
  'salute-dark',
  'salute-light',
]);

export interface LogoProps {
  /** Which logo variant to render. Default: 'transparent' */
  variant?: LogoVariant;
  /** Height in pixels. Width scales proportionally. Default: 32 */
  size?: number;
  /** Whether to show "Agent Commander" text next to the logo. Default: false */
  showText?: boolean;
  /** Additional CSS classes for the wrapper element */
  className?: string;
  /** When true, automatically switch to dark-background variant in dark mode.
   *  Mapping: 'transparent' -> 'dark', 'tight-crop-transparent' -> 'tight-crop-dark',
   *  'attention-light' -> 'attention-dark', 'salute-light' -> 'salute-dark'.
   *  Default: false */
  themeAware?: boolean;
}

const variantMap: Record<LogoVariant, string> = {
  transparent: logoTransparent,
  dark: logoDark,
  light: logoLight,
  'mono-black': logoMonoBlack,
  'mono-white': logoMonoWhite,
  'tight-crop-dark': logoTightCropDark,
  'tight-crop-transparent': logoTightCropTransparent,
  'static-dark': logoStaticDark,
  'static-light': logoStaticLight,
  'static-transparent': logoStaticTransparent,
  'attention-dark': logoAttentionDark,
  'attention-light': logoAttentionLight,
  'salute-dark': logoSaluteDark,
  'salute-light': logoSaluteLight,
};

const Logo: React.FC<LogoProps> = ({
  variant = 'transparent',
  size = 32,
  showText = false,
  className = '',
  themeAware = false,
}) => {
  // Always call the hook to avoid rules-of-hooks violations.
  // The result is only used when themeAware is true.
  let resolvedTheme: 'light' | 'dark' = 'light';
  try {
    resolvedTheme = useThemeStore((state) => state.resolvedTheme);
  } catch (_e) {
    // Gracefully handle cases where theme store is not available
    resolvedTheme = 'light';
  }

  // Map variant to dark equivalent when in dark mode and themeAware is enabled
  let effectiveVariant: LogoVariant = variant;
  if (themeAware && resolvedTheme === 'dark') {
    if (variant === 'transparent') effectiveVariant = 'dark';
    else if (variant === 'tight-crop-transparent') effectiveVariant = 'tight-crop-dark';
    else if (variant === 'attention-light') effectiveVariant = 'attention-dark';
    else if (variant === 'salute-light') effectiveVariant = 'salute-dark';
    else if (variant === 'static-light') effectiveVariant = 'static-dark';
    else if (variant === 'static-transparent') effectiveVariant = 'static-dark';
  }

  const src = variantMap[effectiveVariant];
  const isAnimated = ANIMATED_VARIANTS.has(effectiveVariant);

  // Animated SVGs need <object> for CSS animations to play; static ones use <img>
  const element = isAnimated ? (
    <object
      data={src}
      type="image/svg+xml"
      aria-label="Agent Commander logo"
      style={{ height: `${size}px`, width: `${size}px`, pointerEvents: 'none' }}
    />
  ) : (
    <img
      src={src}
      alt="Agent Commander logo"
      style={{ height: `${size}px` }}
    />
  );

  if (showText) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {element}
        <span className="font-bold text-gray-900 dark:text-warm-white">Agent Commander</span>
      </div>
    );
  }

  return (
    <div className={className}>
      {element}
    </div>
  );
};

export default Logo;
